import type { FC } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getLineByIdSB, getLinePageAnalyticsEvents, selectUserCurrency, useCancelRequest, usePrevious } from '@mwl/core-lib';
import { BreadcrumbsItem, NoSSR } from '@mwl/ui';
import HomeIcon from '@public/assets/common/icons/home.svg';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { SportIcon } from '@/components/SportIcon/SportIcon';
import { routes } from '@/constants';
import { useIsCyberSportPage, useMediaWidthDown, useTypedSelector } from '@/hooks';
import { MatchInfo } from '../MatchInfo/MatchInfo';
import type { HeaderProps } from './Header.types';
import styles from './Header.module.scss';
const BaseHeader: FC<HeaderProps> = ({
  line
}) => {
  const cancelRequest = useCancelRequest();
  const router = useRouter();
  const [widgets, setWidgets] = useState(line.widgets);
  const [isShowShadow, setIsShowShadow] = useState(true);
  const prevStage = usePrevious(line.stage);
  const isXsScreen = useMediaWidthDown('screen850');
  const currency = useTypedSelector(selectUserCurrency);
  const isCyberSportPage = useIsCyberSportPage();
  const pathname = isCyberSportPage ? routes.esports.home : routes.sport.home;
  const fetchLineData = useCallback(async () => {
    const {
      data
    } = await getLineByIdSB(line.id);
    if (!cancelRequest.current && data?.widgets) {
      setWidgets(data.widgets);
    }
  }, [line.id, cancelRequest]);
  const handleClickBack = () => {
    if (!isXsScreen) {
      return;
    }
    router.back();
  };
  const handleXReachEnd = useCallback(() => {
    setIsShowShadow(false);
  }, []);
  const handleScrollLeft = useCallback(() => {
    setIsShowShadow(true);
  }, []);
  const analytics = getLinePageAnalyticsEvents(router.asPath, line);
  useEffect(() => {
    if ((prevStage !== line.stage || JSON.stringify(line.widgets) !== JSON.stringify(widgets)) && currency) {
      fetchLineData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.stage, line.widgets, prevStage, fetchLineData]);
  return <div className={styles.root} data-sentry-component="BaseHeader" data-sentry-source-file="Header.tsx">
      <div className={cn(styles.header, {
      [styles.withShadow]: isShowShadow
    })}>
        <Breadcrumbs classes={{
        breadcrumbsItem: {
          separator: styles.separator,
          itemWrapper: styles.itemWrapper
        }
      }} isLastActive onXReachEnd={handleXReachEnd} onScrollLeft={handleScrollLeft} data-sentry-element="Breadcrumbs" data-sentry-source-file="Header.tsx">
          <BreadcrumbsItem analytics={analytics.bc_mainPage} href={pathname} linkComponent={Link} data-sentry-element="BreadcrumbsItem" data-sentry-source-file="Header.tsx">
            <HomeIcon className={styles.homeIcon} data-sentry-element="HomeIcon" data-sentry-source-file="Header.tsx" />
          </BreadcrumbsItem>

          <BreadcrumbsItem href={{
          pathname,
          query: {
            sp: line.sport.id
          }
        }} className={styles.sportLink} fontSize="sm" linkComponent={Link} analytics={analytics.bc_sportCategory} data-sentry-element="BreadcrumbsItem" data-sentry-source-file="Header.tsx">
            <SportIcon url={line.sport.icon} size={16} data-sentry-element="SportIcon" data-sentry-source-file="Header.tsx" />
            {line.sport.title}
          </BreadcrumbsItem>

          {!!line.category?.title && <BreadcrumbsItem href={{
          pathname,
          query: {
            sp: line.sport.id,
            ct: line.category.id
          }
        }} className={styles.sportLink} fontSize="sm" linkComponent={Link} analytics={analytics.bc_category}>
              {line.category.title}
            </BreadcrumbsItem>}

          <BreadcrumbsItem href={{
          pathname,
          query: {
            sp: line.sport.id,
            ct: line.category.id,
            ch: line.championship.id
          }
        }} onClickSeparator={handleClickBack} analyticsSeaprator={analytics?.bc_back} className={cn(styles.sportLink, styles.championship)} fontSize="sm" linkComponent={Link} analytics={analytics.bc_championships} data-sentry-element="BreadcrumbsItem" data-sentry-source-file="Header.tsx">
            {line.championship.title}
          </BreadcrumbsItem>
        </Breadcrumbs>
      </div>
      {/* NOTE: Внутри компонента используется хук useLineBehaviorV2, логика которого завязана на получение сookie трансляции и виджетов.
        По этому возникает ошибка не соответствия серверного и клиентского рендера.
        После удаления временных сookie - можно будет удалить и NoSSR.
        Ссылка на задачу с добавлением временных сookie - https://jira.dats.tech/browse/MWL-15286.
       */}
      <NoSSR data-sentry-element="NoSSR" data-sentry-source-file="Header.tsx">
        <MatchInfo line={line} data-sentry-element="MatchInfo" data-sentry-source-file="Header.tsx" />
      </NoSSR>
    </div>;
};
export const Header = memo(BaseHeader);
Header.displayName = 'Header';